// @tier: PREMIUM — Historique des annonces admin (Vue pure, logique dans hooks/trSentNotifications.js)
// Même gabarit que /notifications (frontNotifications.jsx) : titre, filtres, tableau + cartes mobile.
import {
  ArrowLeft,
  Bell,
  Clock,
  Copy,
  Eye,
  Filter,
  Loader2,
  Megaphone,
  MoreHorizontal,
  RefreshCw,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ListPagination from '../components/ListPagination';
import PageError from '../components/PageError';
import useSentNotifications, { PERIODS, RECIPIENT_LABELS, audienceLabel } from '../hooks/trSentNotifications';
import { recipientNoun } from '../hooks/trBroadcast';
import { formatTimestamp } from '../hooks/trNotifications';
import { PAGINATION, UI } from '../config/constants';
import { PRIORITY_CONFIG, PRIORITY_VALUES, getPriorityConfig } from '../utils/notificationPriority';

const STATUS_CONFIG = {
  scheduled: { label: 'Programmée', className: 'bg-blue-100 text-blue-700' },
  sending: { label: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
  sent: { label: 'Envoyée', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulée', className: 'bg-gray-100 text-gray-700' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-700' },
};

const LANGUAGE_LABELS = { fr: 'Français', en: 'English', ar: 'العربية' };

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const PriorityBadge = ({ priority }) => {
  const config = getPriorityConfig(priority);
  return <span className={`px-2 py-0.5 rounded-full text-xs ${config.bg} ${config.text}`}>{config.label}</span>;
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.sent;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>{config.label}</span>;
};

const ReadCount = ({ readCount, recipientsCount }) => (
  <span className="text-sm text-gray-900 whitespace-nowrap">
    {readCount} / {recipientsCount}
  </span>
);

const Counter = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 p-3">
    <dt className="text-xs text-gray-600">{label}</dt>
    <dd className="text-lg font-semibold text-gray-900">{value}</dd>
  </div>
);

const DELETED_LABELS = {
  routes: 'Circuit supprimé',
  stops: 'Arrêt supprimé',
  custom: { parent: 'Parent supprimé', driver: 'Chauffeur supprimé' },
};

const deletedLabel = (mode, recipientType) =>
  mode === 'custom' ? DELETED_LABELS.custom[recipientType] || DELETED_LABELS.custom.parent : DELETED_LABELS[mode];

const ANNOUNCEMENT_COLOR = 'text-blue-600 bg-blue-50 border-blue-200';

const RowActions = ({ item, onOpen, onDuplicate }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        aria-label="Actions"
        onClick={(event) => event.stopPropagation()}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
      >
        <MoreHorizontal size={16} />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48" onClick={(event) => event.stopPropagation()}>
      <DropdownMenuItem onClick={() => onOpen(item.id)}>
        <Eye size={14} className="mr-2" />
        Voir le détail
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onDuplicate(item.id)}>
        <Copy size={14} className="mr-2" />
        Dupliquer
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const FILTER_PILL_ACTIVE = 'bg-blue-100 text-blue-800';

const SentNotifications = () => {
  const {
    items,
    searchTerm,
    setSearchTerm,
    pagination,
    page,
    totalPages,
    loading,
    error,
    priority,
    recipient,
    period,
    hasActiveFilters,
    setPriority,
    setRecipient,
    setPeriod,
    resetFilters,
    goToPage,
    listTopRef,
    selectedId,
    detail,
    detailLoading,
    openDetail,
    closeDetail,
    refresh,
    duplicate,
    duplicateById,
    newBroadcast,
    back,
  } = useSentNotifications();

  const languages = detail ? Object.keys(detail.content) : [];
  const filtered = hasActiveFilters || Boolean(searchTerm.trim());

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications envoyées</h1>
          <p className="text-gray-600 mt-1">Les annonces envoyées aux parents de votre école</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={back}>
            <ArrowLeft className="w-4 h-4" />
            Retour aux notifications
          </Button>
          <Button onClick={newBroadcast}>
            <Megaphone className="w-4 h-4" />
            Nouvelle annonce
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher une annonce..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Période">
              {PERIODS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => setPeriod(item.value)}
                  aria-pressed={period === item.value}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    period === item.value ? FILTER_PILL_ACTIVE : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <select
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
              aria-label="Filtrer par destinataire"
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Tous les destinataires</option>
              {Object.entries(RECIPIENT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
              aria-label="Filtrer par priorité"
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Toutes priorités</option>
              {[...PRIORITY_VALUES].reverse().map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_CONFIG[value].label}
                </option>
              ))}
            </select>
            <button
              onClick={refresh}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
              aria-label="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      <div ref={listTopRef} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && <PageError error={error} onRetry={refresh} variant="inline" />}
        {!error && (
          <>
            {/* Vue cartes — mobile uniquement */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Chargement...</div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-gray-500">Aucune annonce trouvée</div>
              ) : (
                items.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => openDetail(item.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className={`p-2 rounded-lg border shrink-0 ${ANNOUNCEMENT_COLOR}`}>
                        <Megaphone className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 break-words">{item.title}</p>
                        <p className="text-xs text-gray-500 truncate">Par {item.author || '—'}</p>
                      </div>
                      <RowActions item={item} onOpen={openDetail} onDuplicate={duplicateById} />
                    </div>
                    <div className="mt-2 ml-[52px] flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatTimestamp(item.sent_at || item.created_at)}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                        {audienceLabel(item.audience, item.recipient_type, item.recipients_count)}
                      </span>
                      <PriorityBadge priority={item.priority} />
                    </div>
                    <div className="mt-2 ml-[52px] text-xs text-gray-500">
                      Lues : <ReadCount readCount={item.read_count} recipientsCount={item.recipients_count} />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Tableau — desktop uniquement */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-[800px] w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Annonce
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                      Destinataires
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Priorité
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Lues
                    </th>
                    <th className="px-4 py-3 w-24 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        Chargement...
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune annonce trouvée</h3>
                        <p className="text-sm text-gray-500">
                          {filtered
                            ? 'Aucune annonce ne correspond aux critères de recherche.'
                            : "Aucune annonce n'a encore été envoyée."}
                        </p>
                        {filtered && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => {
                              setSearchTerm('');
                              resetFilters();
                            }}
                          >
                            Réinitialiser les filtres
                          </Button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} onClick={() => openDetail(item.id)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-4 py-4">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg border shrink-0 ${ANNOUNCEMENT_COLOR}`}>
                              <Megaphone className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[280px]">
                                Par {item.author || '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {audienceLabel(item.audience, item.recipient_type, item.recipients_count)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <PriorityBadge priority={item.priority} />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatTimestamp(item.sent_at || item.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <ReadCount readCount={item.read_count} recipientsCount={item.recipients_count} />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end">
                            <RowActions item={item} onOpen={openDetail} onDuplicate={duplicateById} />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-gray-100">
              <ListPagination
                page={page}
                totalPages={totalPages}
                totalItems={pagination?.total_items ?? 0}
                pageSize={PAGINATION.BROADCASTS_LIMIT}
                onPageChange={goToPage}
              />
            </div>
          </>
        )}
      </div>

      {/* Détail */}
      <Sheet open={Boolean(selectedId)} onOpenChange={(open) => !open && closeDetail()}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{detail?.content.fr.title ?? 'Annonce'}</SheetTitle>
            <SheetDescription>
              {detail
                ? `${formatDateTime(detail.sent_at || detail.created_at)} · ${detail.author || 'Auteur supprimé'}`
                : ' '}
            </SheetDescription>
          </SheetHeader>

          {detailLoading || !detail ? (
            <div className="p-4 text-gray-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Chargement…
            </div>
          ) : (
            <div className="px-4 pb-6 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={detail.priority} />
                <StatusBadge status={detail.status} />
              </div>

              {languages.length > 1 ? (
                <Tabs defaultValue="fr">
                  <TabsList>
                    {languages.map((language) => (
                      <TabsTrigger key={language} value={language}>
                        {LANGUAGE_LABELS[language]}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {languages.map((language) => (
                    <TabsContent key={language} value={language} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                      <p className="font-semibold text-gray-900">{detail.content[language].title}</p>
                      <p className="text-gray-700 whitespace-pre-line">{detail.content[language].message}</p>
                    </TabsContent>
                  ))}
                </Tabs>
              ) : (
                <div>
                  <p className="text-gray-700 whitespace-pre-line">{detail.content.fr.message}</p>
                </div>
              )}

              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Audience</h3>
                <p className="text-sm text-gray-700">
                  {audienceLabel(detail.audience, detail.recipient_type, detail.recipients_count)}
                </p>
                {detail.audience.items.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
                    {detail.audience.items.slice(0, UI.AUDIENCE_LIST_PREVIEW).map((item) => (
                      <li key={item.id} className={item.deleted ? 'italic text-gray-500' : undefined}>
                        {item.deleted ? deletedLabel(detail.audience.mode, detail.recipient_type) : item.name}
                      </li>
                    ))}
                    {detail.audience.items.length > UI.AUDIENCE_LIST_PREVIEW && (
                      <li className="text-gray-500">
                        et {detail.audience.items.length - UI.AUDIENCE_LIST_PREVIEW} autre
                        {detail.audience.items.length - UI.AUDIENCE_LIST_PREVIEW > 1 ? 's' : ''}
                      </li>
                    )}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Suivi</h3>
                <dl className="grid grid-cols-2 gap-2">
                  <Counter label="Destinataires" value={detail.counters.total} />
                  <Counter label="Lues" value={detail.counters.read} />
                  <Counter label="Livrées" value={detail.counters.delivered} />
                  <Counter label="Envoyées" value={detail.counters.sent} />
                  <Counter label="En attente" value={detail.counters.pending} />
                  <Counter label="Échecs" value={detail.counters.failed} />
                </dl>
                <p className="text-sm text-gray-600 mt-2">
                  Dont {detail.reachable_count} {recipientNoun(detail.recipient_type, detail.reachable_count)} joignable
                  {detail.reachable_count > 1 ? 's' : ''} au moment de l&apos;envoi.
                </p>
              </section>

              <Button variant="outline" className="w-full" onClick={() => duplicate(detail)}>
                <Copy className="w-4 h-4" />
                Dupliquer
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SentNotifications;
